/*
 * Small shared pieces: the dialog, status words, and the empty / error /
 * loading states every workspace uses the same way.
 */

import { useEffect, useId, useRef, type ReactNode } from "react";
import type { CommissionState, ReferralStatus, WithdrawalStatus } from "../api/client";
import { humanize } from "./format";
import { AlertTriangle, RefreshCw, X, type Icon } from "./icons";

export type Notify = (message: string) => void;

/** Copy to the clipboard, falling back to execCommand where the API is blocked (http, old browsers). */
export async function copyText(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    document.execCommand("copy");
    area.remove();
  }
}

/* ---------- status words ---------- */

export type Tone = "green" | "amber" | "red" | "gold" | "muted" | "ink";

export function Status({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={`status is-${tone}`}>
      <i aria-hidden="true" />
      {children}
    </span>
  );
}

export const REFERRAL_STATUS: Record<ReferralStatus, { label: string; tone: Tone }> = {
  submitted: { label: "Submitted", tone: "ink" },
  contacted: { label: "Contacted", tone: "gold" },
  converted: { label: "Converted", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
};

export const COMMISSION_STATE: Record<CommissionState, { label: string; tone: Tone }> = {
  in_progress: { label: "In progress", tone: "gold" },
  pending: { label: "Pending", tone: "amber" },
  available: { label: "Available", tone: "green" },
  reversed: { label: "Reversed", tone: "red" },
};

export const WITHDRAWAL_STATUS: Record<WithdrawalStatus, { label: string; tone: Tone }> = {
  requested: { label: "Requested", tone: "amber" },
  approved: { label: "Approved", tone: "gold" },
  paid: { label: "Paid", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  cancelled: { label: "Cancelled", tone: "muted" },
};

/**
 * A set-off (paying your own invoice from the wallet) uses the same statuses,
 * but never waits for approval and can't be cancelled, so they read differently.
 */
export const SETOFF_STATUS: Record<WithdrawalStatus, { label: string; tone: Tone }> = {
  requested: { label: "Processing", tone: "amber" },
  approved: { label: "Processing", tone: "amber" },
  paid: { label: "Applied", tone: "green" },
  rejected: { label: "Returned", tone: "muted" },
  cancelled: { label: "Returned", tone: "muted" },
};

/** A client's account status: the contract leaves the list open ("…"). */
export function accountStatus(status: string): { label: string; tone: Tone } {
  switch (status) {
    case "active":
      return { label: "Active", tone: "green" };
    case "onboarding":
      return { label: "Onboarding", tone: "amber" };
    case "suspended":
      return { label: "Suspended", tone: "red" };
    case "closed":
      return { label: "Closed", tone: "muted" };
    default:
      return { label: humanize(status), tone: "muted" };
  }
}

/** A client's subscription status, also an open list. */
export function subscriptionStatus(status: string | null): { label: string; tone: Tone } {
  switch (status) {
    case null:
      return { label: "No plan yet", tone: "muted" };
    case "active":
      return { label: "Active", tone: "green" };
    case "trialing":
      return { label: "Trial", tone: "gold" };
    case "past_due":
      return { label: "Payment due", tone: "amber" };
    case "expired":
      return { label: "Expired", tone: "red" };
    case "cancelled":
      return { label: "Cancelled", tone: "muted" };
    default:
      return { label: humanize(status), tone: "muted" };
  }
}

/* ---------- states ---------- */

export function EmptyState({
  icon: Glyph,
  title,
  children,
  action,
}: {
  icon: Icon;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty__icon" aria-hidden="true">
        <Glyph size={18} />
      </span>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="load-error" role="alert">
      <span>
        <AlertTriangle size={16} />
        {message}
      </span>
      <button type="button" className="button button--secondary button--sm" onClick={onRetry}>
        <RefreshCw size={14} /> Try again
      </button>
    </div>
  );
}

/** Rows of placeholder lines shaped like a table while it loads. */
export function SkeletonRows({ rows = 4, label = "Loading" }: { rows?: number; label?: string }) {
  return (
    <div className="skel-rows" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div className="skel-row" key={i}>
          <span className="skel" style={{ width: `${34 + ((i * 17) % 22)}%` }} />
          <span className="skel" style={{ width: "18%" }} />
          <span className="skel" style={{ width: "14%" }} />
        </div>
      ))}
    </div>
  );
}

/* ---------- dialog ---------- */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A modal dialog (centred) or sheet (from the right). Escape and the scrim
 * close it; focus moves in on open, stays inside while open (Tab wraps), and
 * returns to whatever opened it on close. `onClose` may be ignored by the
 * caller while a request is in flight.
 */
export function Dialog({
  title,
  description,
  variant = "modal",
  onClose,
  children,
}: {
  title: string;
  description?: ReactNode;
  variant?: "modal" | "sheet";
  onClose: () => void;
  children: ReactNode;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const first =
      node?.querySelector<HTMLElement>("[data-autofocus]") ??
      node?.querySelector<HTMLElement>(`.dialog__body ${FOCUSABLE}`) ??
      node?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (!items.length) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, []);

  return (
    <div
      className={`scrim${variant === "sheet" ? " scrim--sheet" : ""}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={ref}
        className={variant}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-desc` : undefined}
      >
        <header className="dialog__head">
          <div>
            <h2 id={`${id}-title`}>{title}</h2>
            {description && <p id={`${id}-desc`}>{description}</p>}
          </div>
          <button type="button" className="icon-button" aria-label="Close" onClick={() => onCloseRef.current()}>
            <X size={18} />
          </button>
        </header>
        <div className="dialog__body">{children}</div>
      </div>
    </div>
  );
}
